import { ChevronRightIcon } from 'lucide-react';
import { Dialog, DialogClose, DialogContent, DialogTrigger } from '../atoms/Dialog';
import Input from '../V3/Input';
import { Button } from '../atoms/NewPcButton';
import z from 'zod';
import { zodResolver } from '@hookform/resolvers/zod';
import { useForm } from 'react-hook-form';
import { addHyphenPhoneNumber, removeHyphen } from '@/utils/phone';
import { useCallback, useEffect, useRef, useState } from 'react';
import { CPLAT_API_ROUTES, sellerQueries, usePost } from '@/api';
import GreenCheckIcon from '@/assets/icons/GreenCheckIcon.svg?react';
import { useLocalStorage } from 'usehooks-ts';
import { cn } from '@/utils/tailwind';
import { useToast } from '@/hooks/V3/useToast';
import { useQueryClient } from '@tanstack/react-query';

const formSchema = z.object({
  phone: z.string(),
  verifyCode: z.string(),
});

type FormType = z.infer<typeof formSchema>;

interface Props {
  defaultValue?: string;
}

export const PhoneDialog = ({ defaultValue }: Props) => {
  const [open, setOpen] = useState(false);

  const [localStorageCplatToken] = useLocalStorage('cplatToken', '');

  const TOTAL_SECONDS = 3 * 60; // 3분 = 180초
  const [remaining, setRemaining] = useState(TOTAL_SECONDS);
  const endTimeRef = useRef<number | null>(null);
  const rafRef = useRef<number | null>(null);
  const [smsToken, setSmsToken] = useState<string>();
  const [buttonLabel, setButtonLabel] = useState('인증번호 발송');
  const [codeVerified, setCodeVerified] = useState(false);

  const queryClient = useQueryClient();

  const { showToast } = useToast();

  const {
    register,
    formState: { errors },
    watch,
    setError,
    clearErrors,
    setValue,
  } = useForm<FormType>({ resolver: zodResolver(formSchema), shouldUnregister: true });

  const watchedPhone = watch('phone');
  const watchedVerifyCode = watch('verifyCode');

  const { mutate: onCheckPhone } = usePost(CPLAT_API_ROUTES.checkPhone, undefined, undefined, true);

  const { mutate: onVerifyCode } = usePost(
    CPLAT_API_ROUTES.verifyAuthenticationCode,
    undefined,
    undefined,
    true,
  );

  const { mutate: onSendVerifyCode } = usePost(
    CPLAT_API_ROUTES.sendAuthenticationCode,
    undefined,
    undefined,
    true,
  );

  const { mutate: onUpdateInfo } = usePost(CPLAT_API_ROUTES.updateInfo, undefined, undefined, true);

  const update = useCallback(() => {
    if (!endTimeRef.current) return;

    const now = Date.now();
    const diff = Math.max(0, Math.ceil((endTimeRef.current - now) / 1000));

    setRemaining(diff);

    if (diff <= 0) {
      endTimeRef.current = null;
      cancelAnimationFrame(rafRef.current!);
      return;
    }

    rafRef.current = requestAnimationFrame(update);
  }, []);

  const restart = () => {
    cancelAnimationFrame(rafRef.current!);
    endTimeRef.current = Date.now() + TOTAL_SECONDS * 1000;
    setRemaining(TOTAL_SECONDS);
    rafRef.current = requestAnimationFrame(update);
  };

  const handlePhoneNoChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    let value = event.target.value;
    value = value.replace(/[^\d-]/g, '');
    const digits = removeHyphen(value);

    if (digits.length > 11) return;

    const isnNotIncludePrefixNum = digits.length >= 3 && digits.substring(0, 3) !== '010';

    if (isnNotIncludePrefixNum) {
      setError('phone', {
        type: 'pattern',
        message: '휴대폰 번호는 "010-" 으로 시작해야합니다.',
      });
    } else {
      setError('phone', {});
    }

    value = addHyphenPhoneNumber(digits);

    setValue('phone', value);
  };

  const handleCheckPhone = () => {
    onCheckPhone(
      { phoneNo: removeHyphen(watchedPhone) },
      {
        onSuccess: (response) => {
          const isRegistered = response.data.data[0].isRegistered;
          if (isRegistered) setError('phone', { message: '이미 사용중인 휴대폰 번호입니다.' });
          else {
            clearErrors();
            handleSendVerifyCode();
          }
        },
      },
    );
  };

  const handleSendVerifyCode = () => {
    onSendVerifyCode(
      { phoneNo: removeHyphen(watchedPhone) },
      {
        onSuccess: (response) => {
          clearErrors();
          setButtonLabel('재발송');
          restart();
          setSmsToken(response.data.data[0].smsToken);
        },
        onError: () => {
          setError('verifyCode', { message: '인증번호가 일치하지 않습니다.' });
        },
      },
    );
  };

  useEffect(() => {
    if (watchedVerifyCode?.length === 6) {
      onVerifyCode(
        { smsToken, smsCode: watchedVerifyCode },
        {
          onSuccess: () => {
            setCodeVerified(true);
            clearErrors();
          },
          onError: (error) => {
            setError('verifyCode', {
              message: (error.response?.data as { message: string }).message,
            });
          },
        },
      );
    }
  }, [watchedVerifyCode]);

  useEffect(() => {
    setButtonLabel('인증번호 발송');
    setCodeVerified(false);
  }, [watchedPhone]);

  useEffect(() => {
    if (remaining === 0) setError('verifyCode', { message: '입력시간이 초과되었습니다' });
  }, [remaining]);

  const handleUpdateInfo = () => {
    onUpdateInfo(
      { cplatToken: localStorageCplatToken, phoneNo: removeHyphen(watchedPhone) },
      {
        onSuccess: () => {
          setOpen(false);
          queryClient.invalidateQueries({ queryKey: sellerQueries.all() });
          showToast('success', '휴대폰 번호 변경이 완료되었어요');
        },
        onError: () => showToast('warning', '일시적 오류로 변경을 실패했어요'),
      },
    );
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger className="flex w-full items-center justify-between text-gr-700 Body2M16">
        <span className="text-gr-700 Body2M16">휴대폰 번호</span>
        <div className="flex items-center ">
          {defaultValue ? addHyphenPhoneNumber(defaultValue) : '-'}
          <ChevronRightIcon className="h-5 w-5 text-gr-400" />
        </div>
      </DialogTrigger>
      <DialogContent className="w-[420px] gap-5" noCloseBtn>
        <div className="space-y-1">
          <div className="text-gr-900 Title3S18">휴대폰 번호 변경</div>
          <div className="text-gr-700 Body5M14">아이디로 사용될 새 휴대폰 번호를 입력해 주세요</div>
        </div>
        <div className="flex w-full flex-col gap-3">
          <div className="flex  gap-2 ">
            <Input
              placeholder="휴대폰 번호를 입력해 주세요"
              {...register('phone')}
              Error={{ isError: !!errors.phone?.message, errorMessage: errors.phone?.message }}
              onChange={handlePhoneNoChange}
            />
            <Button
              disabled={!watchedPhone || codeVerified}
              onClick={handleCheckPhone}
              variant="line"
            >
              {buttonLabel}
            </Button>
          </div>
          {buttonLabel === '재발송' && (
            <Input
              placeholder="인증번호를 입력해 주세요"
              InputProps={{
                endAdornment: codeVerified ? (
                  <GreenCheckIcon />
                ) : (
                  <span className={cn({ 'text-red-400': remaining === 0 })}>
                    {formatTime(remaining)}
                  </span>
                ),
                endAdornmentStyle: 'text-blue-400 Body5M14 right-2',
              }}
              disabled={codeVerified}
              Error={{
                isError: !!errors.verifyCode?.message,
                errorMessage: errors.verifyCode?.message,
              }}
              {...register('verifyCode')}
              maxLength={6}
              autoComplete="off"
            />
          )}
        </div>
        <div className="flex gap-3">
          <DialogClose asChild>
            <Button variant="secondary" className="flex-1">
              취소
            </Button>
          </DialogClose>
          <Button className="flex-1" disabled={!codeVerified} onClick={handleUpdateInfo}>
            변경
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};

const formatTime = (seconds: number) => {
  const mm = String(Math.floor(seconds / 60)).padStart(2, '0');
  const ss = String(seconds % 60).padStart(2, '0');
  return `${mm}:${ss}`;
};
